import { useState, useEffect } from 'react';
import { eventsApi } from '../lib/api';
import { supabase } from '../lib/supabase';
import EventDashboard from '../components/event/EventDashboard';
import CompanyManager from '../components/event/CompanyManager';
import SubmissionsList from '../components/event/SubmissionsList';
import AssignmentMatrix from '../components/event/AssignmentMatrix';
import NDATracker from '../components/event/NDATracker';
import CompanyInviteEmailer from '../components/event/CompanyInviteEmailer';
import OrganizerStatusDashboard from '../components/event/OrganizerStatusDashboard';

const STATUS_LABELS = {
  draft: 'Draft', open: 'Open', submissions_closed: 'Submissions Closed',
  matching: 'Matching', assigned: 'Assigned', booking: 'Booking',
  active: 'Active', completed: 'Completed', archived: 'Archived',
};

const cardStyle = {
  background: 'rgba(255,255,255,0.97)', backdropFilter: 'blur(24px)',
  border: '1px solid #E2E8F0', borderRadius: 20,
  boxShadow: '0 8px 32px rgba(0,0,0,0.08)', padding: 24,
};

const btnPrimary = {
  padding: '12px 24px', borderRadius: 14, background: '#c9a84c', color: 'white',
  fontSize: 14, fontWeight: 600, fontFamily: 'Poppins, sans-serif',
  border: 'none', cursor: 'pointer', transition: 'all 0.15s',
};

const btnTertiary = {
  padding: '12px 24px', borderRadius: 14, background: 'transparent', color: '#0F172A',
  fontSize: 14, fontWeight: 600, fontFamily: 'Poppins, sans-serif',
  border: '2px solid #E2E8F0', cursor: 'pointer',
};

const inputStyle = {
  width: '100%', padding: '10px 14px', borderRadius: 12,
  border: '2px solid #E2E8F0', fontSize: 14, fontFamily: 'Poppins, sans-serif',
  background: 'rgba(255,255,255,0.7)', color: '#0F172A', outline: 'none',
  boxSizing: 'border-box', transition: 'border-color 0.2s',
};

const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: '#0F172A', marginBottom: 4, fontFamily: 'Poppins, sans-serif' };

export default function EventOrganizer({ onCreateEvent, onEditEventHomepage }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [eventDetail, setEventDetail] = useState(null);
  const [eventHomepage, setEventHomepage] = useState(null);
  const [tab, setTab] = useState('status');
  const [showNew, setShowNew] = useState(false);
  const [newForm, setNewForm] = useState({ name: '', description: '', start_date: '', end_date: '', submission_deadline: '', flat_fee_amount: '', revenue_share_pct: '20', terms_text: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadEvents(); }, []);

  const loadEvents = async () => {
    try { const data = await eventsApi.list(); setEvents(data.events || []); }
    catch (err) { console.error(err); }
    setLoading(false);
  };

  const loadEventDetail = async (id) => {
    try { const data = await eventsApi.get(id); setEventDetail(data); }
    catch (err) { console.error(err); }
    // Check if this event has a homepage
    try {
      const { data: hp } = await supabase
        .from('event_pages')
        .select('*')
        .eq('pitch_event_id', id)
        .maybeSingle();
      setEventHomepage(hp);
    } catch (err) { console.error(err); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newForm.name || !newForm.start_date || !newForm.end_date) return;
    setSaving(true);
    try {
      const payload = { ...newForm };
      if (payload.flat_fee_amount) payload.flat_fee_amount = parseFloat(payload.flat_fee_amount);
      if (payload.revenue_share_pct) payload.revenue_share_pct = parseFloat(payload.revenue_share_pct);
      const data = await eventsApi.create(payload);
      setShowNew(false);
      setNewForm({ name: '', description: '', start_date: '', end_date: '', submission_deadline: '', flat_fee_amount: '', revenue_share_pct: '20', terms_text: '' });
      await loadEvents();
      setSelectedEventId(data.event.id);
      await loadEventDetail(data.event.id);
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await eventsApi.update(selectedEventId, { status: newStatus });
      await loadEventDetail(selectedEventId);
      await loadEvents();
    } catch (err) { console.error(err); }
  };

  const TABS = [
    { key: 'status', label: 'Status' },
    { key: 'dashboard', label: 'Overview' },
    { key: 'homepage', label: 'Homepage' },
    { key: 'companies', label: 'Companies' },
    { key: 'submissions', label: 'Submissions' },
    { key: 'matching', label: 'Matching' },
    { key: 'ndas', label: 'NDAs' },
    { key: 'invitations', label: 'Invitations' },
  ];

  // Event detail view
  if (selectedEventId && eventDetail) {
    const ev = eventDetail.event;
    const formUrl = `${window.location.origin}/event/${ev.id}/submit`;

    return (
      <div>
        <button onClick={() => { setSelectedEventId(null); setEventDetail(null); setTab('status'); }}
          style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: '#94A3B8', background: 'none', border: 'none', cursor: 'pointer', marginBottom: 16, fontFamily: 'Poppins, sans-serif' }}>
          ← Back to Events
        </button>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
          <div>
            <h1 style={{ fontFamily: '"EB Garamond", serif', fontSize: 28, fontWeight: 700, color: '#0F172A', margin: 0 }}>{ev.name}</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
              <span style={{ padding: '4px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600, background: '#EEF2FF', color: '#4F46E5', fontFamily: 'Poppins, sans-serif' }}>
                {STATUS_LABELS[ev.status]}
              </span>
              <span style={{ fontSize: 13, color: '#94A3B8', fontFamily: 'Poppins, sans-serif' }}>
                {new Date(ev.start_date).toLocaleDateString()} - {new Date(ev.end_date).toLocaleDateString()}
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => navigator.clipboard.writeText(formUrl)} style={{ ...btnTertiary, padding: '8px 16px', fontSize: 12 }}>
              Copy Form Link
            </button>
            <button onClick={async () => {
              if (!window.confirm(`Delete "${ev.name}"? This cannot be undone.`)) return;
              try {
                await eventsApi.delete(ev.id);
                setSelectedEventId(null);
                setEventDetail(null);
                loadEvents();
              } catch (err) {
                alert('Failed to delete event: ' + err.message);
              }
            }} style={{ ...btnTertiary, padding: '8px 16px', fontSize: 12, color: '#F97066', borderColor: '#F97066' }}>
              Delete Event
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, background: '#F1F5F9', borderRadius: 14, padding: 4, width: 'fit-content', marginBottom: 24 }}>
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              padding: '8px 20px', borderRadius: 12, fontSize: 13, fontWeight: 500,
              fontFamily: 'Poppins, sans-serif', border: 'none', cursor: 'pointer', transition: 'all 0.2s',
              background: tab === t.key ? 'white' : 'transparent',
              color: tab === t.key ? '#0F172A' : '#64748B',
              boxShadow: tab === t.key ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
            }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {tab === 'status' && (
          <OrganizerStatusDashboard eventId={selectedEventId} />
        )}
        {tab === 'dashboard' && (
          <EventDashboard event={ev} eventDetail={eventDetail} onStatusChange={handleStatusChange} formUrl={formUrl} />
        )}
        {tab === 'companies' && (
          <CompanyManager eventId={selectedEventId} eventDetail={eventDetail} onRefresh={() => loadEventDetail(selectedEventId)} />
        )}
        {tab === 'submissions' && (
          <SubmissionsList eventId={selectedEventId} onRefresh={() => loadEventDetail(selectedEventId)} />
        )}
        {tab === 'matching' && (
          <AssignmentMatrix eventId={selectedEventId} onRefresh={() => loadEventDetail(selectedEventId)} />
        )}
        {tab === 'ndas' && (
          <NDATracker eventId={selectedEventId} />
        )}
        {tab === 'homepage' && (
          <div>
            {eventHomepage ? (
              <div>
                <div style={cardStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                    <div>
                      <h2 style={{ fontFamily: '"EB Garamond", serif', fontSize: 22, fontWeight: 700, color: '#0F172A', margin: 0 }}>Event Homepage</h2>
                      <span style={{ padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: eventHomepage.status === 'published' ? '#ECFDF5' : '#EEF2FF', color: eventHomepage.status === 'published' ? '#059669' : '#4F46E5', fontFamily: 'Poppins, sans-serif', display: 'inline-block', marginTop: 6 }}>
                        {eventHomepage.status === 'published' ? 'Published' : 'Draft'}
                      </span>
                    </div>
                    <button onClick={() => onEditEventHomepage && onEditEventHomepage(eventHomepage.id)} style={btnPrimary}>
                      Edit Homepage
                    </button>
                  </div>

                  <div style={{ borderTop: '1px solid #E2E8F0', paddingTop: 16 }}>
                    <p style={{ ...labelStyle, marginBottom: 8 }}>Public Links</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {[
                        { label: 'Public Page', path: `/event/${eventHomepage.slug}` },
                        { label: 'Company Page', path: `/event/${eventHomepage.slug}/companies` },
                        { label: 'Inventor Page', path: `/event/${eventHomepage.slug}/inventors` },
                      ].map(link => (
                        <div key={link.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#F8FAFC', borderRadius: 12 }}>
                          <div>
                            <span style={{ fontSize: 13, fontWeight: 600, color: '#0F172A', fontFamily: 'Poppins, sans-serif' }}>{link.label}</span>
                            <span style={{ fontSize: 12, color: '#94A3B8', fontFamily: 'Poppins, sans-serif', marginLeft: 8 }}>{link.path}</span>
                          </div>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button onClick={() => navigator.clipboard.writeText(window.location.origin + link.path)} style={{ ...btnTertiary, padding: '6px 12px', fontSize: 11 }}>Copy</button>
                            <button onClick={() => window.open(link.path, '_blank')} style={{ ...btnTertiary, padding: '6px 12px', fontSize: 11 }}>Open</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: 64 }}>
                <div style={{ fontSize: 48, opacity: 0.2, marginBottom: 16 }}>🌐</div>
                <h3 style={{ fontFamily: '"EB Garamond", serif', fontSize: 22, color: '#0F172A', marginBottom: 8 }}>No Event Homepage Yet</h3>
                <p style={{ fontSize: 14, color: '#94A3B8', fontFamily: 'Poppins, sans-serif', marginBottom: 20 }}>Create a public-facing homepage for companies and inventors</p>
                <button onClick={() => onCreateEvent && onCreateEvent(selectedEventId)} style={btnPrimary}>+ Create Event Homepage</button>
              </div>
            )}
          </div>
        )}
        {tab === 'invitations' && eventHomepage && (
          <CompanyInviteEmailer event={eventHomepage} />
        )}
        {tab === 'invitations' && !eventHomepage && (
          <div style={{ textAlign: 'center', padding: 64 }}>
            <p style={{ fontSize: 14, color: '#94A3B8', fontFamily: 'Poppins, sans-serif' }}>Create an Event Homepage first to send invitations.</p>
          </div>
        )}
      </div>
    );
  }

  if (loading) return <div style={{ textAlign: 'center', padding: 64, color: '#94A3B8' }}>Loading...</div>;

  // Event list
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: '"EB Garamond", serif', fontSize: 30, fontWeight: 700, color: '#0F172A', margin: 0 }}>Event Organizer</h1>
          <p style={{ fontSize: 14, color: '#94A3B8', marginTop: 4, fontFamily: 'Poppins, sans-serif' }}>Create and manage virtual pitch events</p>
        </div>
        <button onClick={() => setShowNew(true)} style={btnPrimary}>+ New Event</button>
      </div>

      {events.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 64 }}>
          <div style={{ fontSize: 48, opacity: 0.2, marginBottom: 16 }}>🎪</div>
          <h3 style={{ fontFamily: '"EB Garamond", serif', fontSize: 22, color: '#0F172A', marginBottom: 8 }}>No events yet</h3>
          <p style={{ fontSize: 14, color: '#94A3B8', fontFamily: 'Poppins, sans-serif' }}>Create your first pitch event</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
          {events.map(ev => (
            <button key={ev.id} onClick={() => { setSelectedEventId(ev.id); loadEventDetail(ev.id); setTab('status'); }}
              style={{ ...cardStyle, textAlign: 'left', cursor: 'pointer', width: '100%', transition: 'box-shadow 0.2s' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <h3 style={{ fontFamily: '"EB Garamond", serif', fontSize: 20, fontWeight: 700, color: '#0F172A', margin: 0 }}>{ev.name}</h3>
                <span style={{ padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: '#EEF2FF', color: '#4F46E5', fontFamily: 'Poppins, sans-serif' }}>
                  {STATUS_LABELS[ev.status]}
                </span>
              </div>
              <p style={{ fontSize: 13, color: '#94A3B8', fontFamily: 'Poppins, sans-serif', margin: 0 }}>
                {new Date(ev.start_date).toLocaleDateString()} - {new Date(ev.end_date).toLocaleDateString()}
              </p>
            </button>
          ))}
        </div>
      )}

      {/* New Event Modal */}
      {showNew && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ ...cardStyle, width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h2 style={{ fontFamily: '"EB Garamond", serif', fontSize: 24, fontWeight: 700, color: '#0F172A', margin: 0 }}>New Pitch Event</h2>
              <button onClick={() => setShowNew(false)} style={{ background: 'none', border: 'none', fontSize: 24, color: '#94A3B8', cursor: 'pointer' }}>×</button>
            </div>
            <form onSubmit={handleCreate}>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Event Name *</label>
                <input value={newForm.name} onChange={e => setNewForm({ ...newForm, name: e.target.value })}
                  placeholder="e.g. TCA Virtual Pitch - May 2026" style={inputStyle} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Description</label>
                <textarea value={newForm.description} onChange={e => setNewForm({ ...newForm, description: e.target.value })}
                  rows={3} style={{ ...inputStyle, resize: 'none' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                <div>
                  <label style={labelStyle}>Start Date *</label>
                  <input type="date" value={newForm.start_date} onChange={e => setNewForm({ ...newForm, start_date: e.target.value })} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>End Date *</label>
                  <input type="date" value={newForm.end_date} onChange={e => setNewForm({ ...newForm, end_date: e.target.value })} style={inputStyle} />
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Submission Deadline</label>
                <input type="datetime-local" value={newForm.submission_deadline} onChange={e => setNewForm({ ...newForm, submission_deadline: e.target.value })} style={inputStyle} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                <div>
                  <label style={labelStyle}>Flat Fee ($)</label>
                  <input type="number" step="0.01" value={newForm.flat_fee_amount} onChange={e => setNewForm({ ...newForm, flat_fee_amount: e.target.value })}
                    placeholder="e.g. 150" style={inputStyle} />
                </div>
              </div>
              <div style={{ marginBottom: 24 }}>
                <label style={labelStyle}>Terms of Submission</label>
                <textarea value={newForm.terms_text} onChange={e => setNewForm({ ...newForm, terms_text: e.target.value })}
                  rows={4} placeholder="Paste your terms of submission text here..." style={{ ...inputStyle, resize: 'none', fontSize: 12 }} />
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <button type="button" onClick={() => setShowNew(false)} style={{ ...btnTertiary, flex: 1 }}>Cancel</button>
                <button type="submit" disabled={saving} style={{ ...btnPrimary, flex: 1, opacity: saving ? 0.5 : 1 }}>
                  {saving ? 'Creating...' : 'Create Event'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
